// oxlint-disable prefer-default-export
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { FormattedMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import Select from '@atlaskit/select/default';
import type {
	GroupType,
	InputActionMeta,
	OptionsType,
	OptionType,
	ValueType,
} from '@atlaskit/select/types';

import type { AsyncFieldConfiguration } from '../../../../../../../../common/types/async-fields';
import type {
	FieldValues,
	OptionsValue,
} from '../../../../../../../../common/types/form/form-fields/field-values';
import { toOptions } from '../../../../../../../../common/types/form/form-fields/utils';
import { CustomSelectOption } from '../../../../../../../../common/ui/custom-select-option';
import {
	getDropDownMenuPosition,
	haveOtherFieldValuesChanged,
} from '../../../../../../../../common/utils';
import { messages } from './messages';

const LOAD_OPTIONS_DEBOUNCE_WAIT_MS = 500;
const FIRST_PAGE_START_INDEX = 0;

// Defined at module scope so react-select keeps a stable reference and does not remount the menu
// (which would reset the infinite-scroll position) on each parent render.
const selectComponents = {
	Option: CustomSelectOption<true>,
};

interface PaginatedEditMultipleSelectInputProps {
	fieldId: string;
	fieldInputId: string;
	fieldErrorId: string;
	fieldAsyncConfiguration: AsyncFieldConfiguration;
	currentValue: OptionsValue | undefined;
	currentValues: FieldValues;
	isInvalid: boolean;
	isRequired: boolean;
	onChange: (value: ValueType<OptionType, true>) => void;
}

const hasDefaultOptionsEnabled = ({ defaultOptions }: AsyncFieldConfiguration): boolean =>
	defaultOptions === true;

const shouldLoadOptionsWhenMenuOpens = ({ defaultOptions }: AsyncFieldConfiguration): boolean =>
	defaultOptions === undefined;

export const PaginatedEditMultipleSelectInput = ({
	fieldId,
	fieldInputId,
	fieldErrorId,
	fieldAsyncConfiguration,
	currentValue,
	currentValues,
	isInvalid,
	isRequired,
	onChange,
}: PaginatedEditMultipleSelectInputProps): React.JSX.Element => {
	di(debounce);
	const intl = useIntl();

	const [asyncOptions, setAsyncOptions] = useState<OptionsType<OptionType>>([]);
	const [inputValue, setInputValue] = useState('');
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const requestCounterRef = useRef(0);
	const lastLoadedValuesRef = useRef<FieldValues | undefined>(undefined);
	const lastLoadedConfigRef = useRef<AsyncFieldConfiguration | undefined>(undefined);
	const paginationRef = useRef({
		startIndex: FIRST_PAGE_START_INDEX,
		hasNextPage: false,
		query: '',
	});

	const invalidateLoadedOptions = useCallback(() => {
		requestCounterRef.current += 1;
		setAsyncOptions([]);
		paginationRef.current = {
			query: '',
			startIndex: FIRST_PAGE_START_INDEX,
			hasNextPage: false,
		};
		setIsLoading(false);
		lastLoadedValuesRef.current = undefined;
	}, []);

	// Invalidate cached options when parent field values change or config changes
	useEffect(() => {
		const previousValues = lastLoadedValuesRef.current;
		const hasRelevantParentValueChanged =
			previousValues !== undefined &&
			haveOtherFieldValuesChanged(previousValues, currentValues, fieldId);
		const hasConfigChanged = lastLoadedConfigRef.current !== fieldAsyncConfiguration;

		if (hasRelevantParentValueChanged || hasConfigChanged) {
			invalidateLoadedOptions();
		}
	}, [currentValues, fieldAsyncConfiguration, fieldId, invalidateLoadedOptions]);

	const loadFirstPage = useCallback(
		(query: string) => {
			if (!fieldAsyncConfiguration.loadPaginatedOptions) {
				return;
			}

			requestCounterRef.current += 1;
			const requestId = requestCounterRef.current;

			setIsLoading(true);
			paginationRef.current = {
				query,
				startIndex: FIRST_PAGE_START_INDEX,
				hasNextPage: false,
			};

			fieldAsyncConfiguration
				.loadPaginatedOptions(query, currentValues, fieldId, FIRST_PAGE_START_INDEX)
				.then((result) => {
					if (requestId !== requestCounterRef.current) {
						return;
					}

					setAsyncOptions(result.options as OptionsType<OptionType>);
					paginationRef.current = {
						query,
						startIndex: result.options.length,
						hasNextPage: result.hasNextPage,
					};
					lastLoadedValuesRef.current = currentValues;
					lastLoadedConfigRef.current = fieldAsyncConfiguration;
				})
				.catch(() => {
					if (requestId !== requestCounterRef.current) {
						return;
					}

					setAsyncOptions([]);
					paginationRef.current = {
						query,
						startIndex: FIRST_PAGE_START_INDEX,
						hasNextPage: false,
					};
				})
				.finally(() => {
					if (requestId !== requestCounterRef.current) {
						return;
					}

					setIsLoading(false);
				});
		},
		[fieldAsyncConfiguration, currentValues, fieldId],
	);

	const debouncedLoadFirstPage = useMemo(
		() => debounce(loadFirstPage, LOAD_OPTIONS_DEBOUNCE_WAIT_MS),
		[loadFirstPage],
	);

	// Cleanup debounced function on unmount
	useEffect(
		() => () => {
			debouncedLoadFirstPage.cancel();
		},
		[debouncedLoadFirstPage],
	);

	// Load options on mount when defaultOptions is true
	useEffect(() => {
		if (hasDefaultOptionsEnabled(fieldAsyncConfiguration)) {
			loadFirstPage('');
		}
	}, [fieldAsyncConfiguration, loadFirstPage]);

	const handleMenuOpen = useCallback(() => {
		setIsMenuOpen(true);

		if (!shouldLoadOptionsWhenMenuOpens(fieldAsyncConfiguration)) {
			return;
		}

		// Only load if we haven't loaded yet, if parent values/config changed, or if the options
		// currently held are still filtered by a previous search keyword. The input is cleared on
		// close, so a previously-searched list must be reloaded unfiltered to stay in sync.
		const previousValues = lastLoadedValuesRef.current;
		const shouldLoad =
			previousValues === undefined ||
			lastLoadedConfigRef.current !== fieldAsyncConfiguration ||
			haveOtherFieldValuesChanged(previousValues, currentValues, fieldId) ||
			paginationRef.current.query !== '';

		if (shouldLoad) {
			loadFirstPage(inputValue);
		}
	}, [fieldAsyncConfiguration, currentValues, fieldId, inputValue, loadFirstPage]);

	const handleMenuClose = useCallback(() => {
		setIsMenuOpen(false);
		// Clear the search keyword so the field returns to its selected/default value display and
		// the next open starts from a clean, unfiltered state (matches the non-paginated AsyncSelect,
		// which clears its internal input on close). The selection itself is unaffected because the
		// displayed value is driven by `currentValue`, not `inputValue`.
		setInputValue('');
		debouncedLoadFirstPage.cancel();
	}, [debouncedLoadFirstPage]);

	const handleInputChange = useCallback(
		(newValue: string, actionMeta: InputActionMeta) => {
			// ADS Select calls onInputChange for blur/menu-close as well as real typing.
			// Only user input should trigger keyword API searches.
			if (actionMeta.action !== 'input-change') {
				return;
			}

			setInputValue(newValue);
			debouncedLoadFirstPage(newValue);
		},
		[debouncedLoadFirstPage],
	);

	const handleMenuScrollToBottom = useCallback(() => {
		const { hasNextPage, startIndex, query } = paginationRef.current;
		if (!hasNextPage || isLoading || !fieldAsyncConfiguration.loadPaginatedOptions) {
			return;
		}

		const requestId = requestCounterRef.current;
		setIsLoading(true);

		fieldAsyncConfiguration
			.loadPaginatedOptions(query, currentValues, fieldId, startIndex)
			.then((result) => {
				if (requestId !== requestCounterRef.current) {
					return;
				}

				setAsyncOptions((prev: OptionsType<OptionType>) => [
					...prev,
					...(result.options as OptionsType<OptionType>),
				]);
				paginationRef.current = {
					query,
					startIndex: startIndex + result.options.length,
					hasNextPage: result.hasNextPage,
				};
			})
			.catch(() => {
				// Keep already-loaded options if an additional page fails
			})
			.finally(() => {
				if (requestId !== requestCounterRef.current) {
					return;
				}

				setIsLoading(false);
			});
	}, [currentValues, fieldAsyncConfiguration, fieldId, isLoading]);

	// Filter out already selected options from the dropdown list
	const selectedValues = new Set(currentValue?.options?.map((opt) => opt.id) ?? []);
	const unselectedOptions = asyncOptions.filter((opt) => !selectedValues.has(String(opt.value)));

	const asyncOptionsWithLabel =
		unselectedOptions.length > 0
			? [
					{
						label: intl.formatMessage(messages.searchResultsLabel, {
							numberOfSearchResults: unselectedOptions.length,
						}),
						options: unselectedOptions,
					},
				]
			: [];

	return (
		<Box testId={`field-input-multiple-select-edit-${fieldId}`}>
			<Box testId={`field-input-multiple-select-edit-async-${fieldId}`}>
				<Select<OptionType, true>
					inputId={fieldInputId}
					options={asyncOptionsWithLabel}
					formatGroupLabel={(group: GroupType<OptionType>) => group.label as React.ReactNode}
					value={toOptions(currentValue?.options) ?? null}
					inputValue={inputValue}
					menuIsOpen={isMenuOpen}
					onMenuOpen={handleMenuOpen}
					onMenuClose={handleMenuClose}
					isMulti
					isLoading={isLoading}
					onChange={onChange}
					onInputChange={handleInputChange}
					onMenuScrollToBottom={handleMenuScrollToBottom}
					isSearchable
					noOptionsMessage={
						fieldAsyncConfiguration.noOptionsMessage ??
						(() => <FormattedMessage {...messages.optionDropdownNoOptions} />)
					}
					placeholder={<FormattedMessage {...messages.optionDropdownSelect} />}
					isInvalid={isInvalid}
					testId={`field-input-multiple-select-edit-input-${fieldId}`}
					isRequired={isRequired}
					filterOption={null}
					{...getDropDownMenuPosition()}
					components={selectComponents}
					descriptionId={
						isInvalid && fg('jsm-accessibility-fixes-new-renderer') ? fieldErrorId : undefined
					}
				/>
			</Box>
		</Box>
	);
};
